/**
 * Comprehensive Error Handling Utilities for Operational Scenarios
 * Provides standardized error handling, logging, and user-friendly error messages
 */

// Error types for categorization
export const ERROR_TYPES = {
  NETWORK: 'NETWORK',
  VALIDATION: 'VALIDATION',
  AI_GENERATION: 'AI_GENERATION',
  DATA_PROCESSING: 'DATA_PROCESSING',
  VISUALIZATION: 'VISUALIZATION',
  DATABASE: 'DATABASE',
  AUTHENTICATION: 'AUTHENTICATION',
  PERMISSION: 'PERMISSION',
  UNKNOWN: 'UNKNOWN'
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL'
};

/**
 * Enhanced Error class with additional context
 */
export class OperationalScenarioError extends Error {
  constructor(message, type = ERROR_TYPES.UNKNOWN, severity = ERROR_SEVERITY.MEDIUM, context = {}) {
    super(message);
    this.name = 'OperationalScenarioError';
    this.type = type;
    this.severity = severity;
    this.context = context;
    this.timestamp = new Date().toISOString();
    this.userMessage = this.generateUserMessage();
  }

  generateUserMessage() {
    const userMessages = {
      [ERROR_TYPES.NETWORK]: 'Problème de connexion réseau. Veuillez vérifier votre connexion et réessayer.',
      [ERROR_TYPES.VALIDATION]: 'Les données fournies ne sont pas valides. Veuillez vérifier vos entrées.',
      [ERROR_TYPES.AI_GENERATION]: 'Erreur de génération IA.',
      [ERROR_TYPES.DATA_PROCESSING]: 'Erreur de traitement des données.',
      [ERROR_TYPES.VISUALIZATION]: 'Erreur lors de l\'affichage de la visualisation. Veuillez actualiser la page.',
      [ERROR_TYPES.DATABASE]: 'Erreur de base de données. Veuillez réessayer plus tard.',
      [ERROR_TYPES.AUTHENTICATION]: 'Problème d\'authentification. Veuillez vous reconnecter.',
      [ERROR_TYPES.PERMISSION]: 'Vous n\'avez pas les permissions nécessaires pour cette action.',
      [ERROR_TYPES.UNKNOWN]: 'Une erreur inattendue s\'est produite. Veuillez réessayer.'
    };

    return userMessages[this.type] || userMessages[ERROR_TYPES.UNKNOWN];
  }
}

/**
 * Error handler for scenario generation
 */
export const handleScenarioGenerationError = (error, context = {}) => {
  console.error('[ScenarioGeneration] Error:', error);

  let errorType = ERROR_TYPES.AI_GENERATION;
  let severity = ERROR_SEVERITY.MEDIUM;
  let userMessage = 'Erreur lors de la génération de scénarios';

  // Analyze error to determine type and severity
  if (error.response) {
    const status = error.response.status;

    if (status >= 500) {
      errorType = ERROR_TYPES.NETWORK;
      severity = ERROR_SEVERITY.HIGH;
      userMessage = 'Erreur serveur lors de la génération. Veuillez réessayer plus tard.';
    } else if (status === 429) {
      errorType = ERROR_TYPES.AI_GENERATION;
      severity = ERROR_SEVERITY.MEDIUM;
      userMessage = 'Trop de demandes de génération. Veuillez attendre avant de réessayer.';
    } else if (status === 400) {
      errorType = ERROR_TYPES.VALIDATION;
      severity = ERROR_SEVERITY.LOW;
      userMessage = 'Données insuffisantes pour la génération. Vérifiez les données CTI.';
    } else if (status === 401 || status === 403) {
      errorType = ERROR_TYPES.AUTHENTICATION;
      severity = ERROR_SEVERITY.HIGH;
      userMessage = 'Problème d\'authentification. Veuillez vous reconnecter.';
    }
  } else if (error.message?.includes('timeout')) {
    errorType = ERROR_TYPES.NETWORK;
    severity = ERROR_SEVERITY.MEDIUM;
    userMessage = 'La génération prend trop de temps. Veuillez réessayer.';
  } else if (error.message?.includes('CTI')) {
    errorType = ERROR_TYPES.VALIDATION;
    severity = ERROR_SEVERITY.LOW;
    userMessage = 'Données CTI insuffisantes. Effectuez d\'abord une analyse CTI.';
  }

  return new OperationalScenarioError(userMessage, errorType, severity, {
    originalError: error.message,
    context,
    timestamp: new Date().toISOString()
  });
};

/**
 * Error handler for phase visualization
 */
export const handleVisualizationError = (error, context = {}) => {
  console.error('[PhaseVisualization] Error:', error);

  let errorType = ERROR_TYPES.VISUALIZATION;
  let severity = ERROR_SEVERITY.MEDIUM;
  let userMessage = 'Erreur lors de l\'affichage de la visualisation';

  if (error.message?.includes('scenario')) {
    errorType = ERROR_TYPES.DATA_PROCESSING;
    userMessage = 'Données de scénario invalides pour la visualisation.';
  } else if (error.message?.includes('React Flow')) {
    errorType = ERROR_TYPES.VISUALIZATION;
    userMessage = 'Erreur de rendu de la visualisation. Veuillez actualiser la page.';
  } else if (error.message?.includes('phase')) {
    errorType = ERROR_TYPES.VALIDATION;
    userMessage = 'Phase sélectionnée invalide ou données manquantes.';
  }

  return new OperationalScenarioError(userMessage, errorType, severity, {
    originalError: error.message,
    context,
    timestamp: new Date().toISOString()
  });
};

/**
 * Error handler for data processing
 */
export const handleDataProcessingError = (error, context = {}) => {
  console.error('[DataProcessing] Error:', error);

  let errorType = ERROR_TYPES.DATA_PROCESSING;
  let severity = ERROR_SEVERITY.MEDIUM;
  let userMessage = 'Erreur lors du traitement des données';

  if (error.message?.includes('validation')) {
    errorType = ERROR_TYPES.VALIDATION;
    severity = ERROR_SEVERITY.LOW;
    userMessage = 'Format de données invalide.';
  } else if (error.message?.includes('memory') || error.message?.includes('performance')) {
    errorType = ERROR_TYPES.DATA_PROCESSING;
    severity = ERROR_SEVERITY.HIGH;
    userMessage = 'Données trop volumineuses. Essayez avec moins de données.';
  }

  return new OperationalScenarioError(userMessage, errorType, severity, {
    originalError: error.message,
    context,
    timestamp: new Date().toISOString()
  });
};

/**
 * Error handler for database operations
 */
export const handleDatabaseError = (error, context = {}) => {
  console.error('[Database] Error:', error);

  let errorType = ERROR_TYPES.DATABASE;
  let severity = ERROR_SEVERITY.HIGH;
  let userMessage = 'Erreur de base de données';

  if (error.response?.status === 404) {
    severity = ERROR_SEVERITY.LOW;
    userMessage = 'Données non trouvées.';
  } else if (error.response?.status === 409) {
    severity = ERROR_SEVERITY.MEDIUM;
    userMessage = 'Conflit de données. L\'élément existe déjà.';
  } else if (error.message?.includes('connection')) {
    severity = ERROR_SEVERITY.HIGH;
    userMessage = 'Problème de connexion à la base de données.';
  }

  return new OperationalScenarioError(userMessage, errorType, severity, {
    originalError: error.message,
    context,
    timestamp: new Date().toISOString()
  });
};

/**
 * Generic error handler with automatic type detection
 */
export const handleGenericError = (error, context = {}) => {
  console.error('[Generic] Error:', error);

  // Try to determine error type from context or error properties
  if (context.operation === 'generation') {
    return handleScenarioGenerationError(error, context);
  } else if (context.operation === 'visualization') {
    return handleVisualizationError(error, context);
  } else if (context.operation === 'database') {
    return handleDatabaseError(error, context);
  } else if (context.operation === 'processing') {
    return handleDataProcessingError(error, context);
  }

  // Default handling
  return new OperationalScenarioError(
    'Une erreur inattendue s\'est produite',
    ERROR_TYPES.UNKNOWN,
    ERROR_SEVERITY.MEDIUM,
    {
      originalError: error.message,
      context,
      timestamp: new Date().toISOString()
    }
  );
};

/**
 * Error recovery suggestions
 */
export const getErrorRecoveryActions = (error) => {
  const actions = {
    [ERROR_TYPES.NETWORK]: [
      'Vérifiez votre connexion internet',
      'Réessayez dans quelques instants',
      'Contactez l\'administrateur si le problème persiste'
    ],
    [ERROR_TYPES.VALIDATION]: [
      'Vérifiez les données saisies',
      'Assurez-vous que tous les champs requis sont remplis',
      'Effectuez une analyse CTI si nécessaire'
    ],
    [ERROR_TYPES.AI_GENERATION]: [
      'Réessayez l\'opération',
      'Vérifiez la disponibilité du service IA'
    ],
    [ERROR_TYPES.DATA_PROCESSING]: [
      'Vérifiez l\'intégrité des données',
      'Réduisez la quantité de données si possible',
      'Actualisez la page et réessayez'
    ],
    [ERROR_TYPES.VISUALIZATION]: [
      'Actualisez la page',
      'Vérifiez que le scénario contient des données valides',
      'Essayez avec un autre scénario'
    ],
    [ERROR_TYPES.DATABASE]: [
      'Réessayez dans quelques instants',
      'Vérifiez votre connexion',
      'Contactez le support technique'
    ],
    [ERROR_TYPES.AUTHENTICATION]: [
      'Reconnectez-vous à l\'application',
      'Vérifiez vos identifiants',
      'Contactez l\'administrateur'
    ],
    [ERROR_TYPES.PERMISSION]: [
      'Contactez l\'administrateur pour obtenir les permissions',
      'Vérifiez votre rôle utilisateur'
    ]
  };

  return actions[error.type] || actions[ERROR_TYPES.UNKNOWN] || [
    'Réessayez l\'opération',
    'Actualisez la page',
    'Contactez le support technique'
  ];
};

/**
 * Log error for monitoring and debugging
 */
export const logError = (error, context = {}) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    error: {
      name: error.name,
      message: error.message,
      type: error.type,
      severity: error.severity,
      stack: error.stack
    },
    context,
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.group(`🚨 Error [${error.severity}] - ${error.type}`);
    console.error('Message:', error.message);
    console.error('Context:', context);
    console.error('Stack:', error.stack);
    console.groupEnd();
  }

  // In production, you would send this to your logging service
  // Example: sendToLoggingService(logEntry);

  return logEntry;
};

/**
 * Create user-friendly error message with recovery actions
 */
export const createErrorMessage = (error, includeActions = true) => {
  const message = {
    title: 'Erreur',
    description: error.userMessage || error.message,
    type: error.type,
    severity: error.severity,
    timestamp: error.timestamp
  };

  if (includeActions) {
    message.recoveryActions = getErrorRecoveryActions(error);
  }

  return message;
};

export default {
  OperationalScenarioError,
  ERROR_TYPES,
  ERROR_SEVERITY,
  handleScenarioGenerationError,
  handleVisualizationError,
  handleDataProcessingError,
  handleDatabaseError,
  handleGenericError,
  getErrorRecoveryActions,
  logError,
  createErrorMessage
};
